import { useFeatureToggles } from '@/shared/useFeatureToggles/useFeatureToggles.ts';
import { FeatureToggles } from '@/shared/useFeatureToggles/featureToggles.ts';
import { computed, ref } from 'vue';
import {
  type Avallinie,
  RahmenVertragsModell,
  type Sublimit,
  SUBLIMIT_ANTEIL,
} from '@/shared/Avallinie/types.ts';
import { DateTimeFormatter } from '@/shared/utilities/DateTimeFormatter/DateTimeFormatter.ts';
import { useAuftraggeberApi } from '@/auftraggeber/api/useAuftraggeberApi';

const dateTimeFormatter = new DateTimeFormatter();
const formatDateTime = dateTimeFormatter.formatiertesLocalDateTime;

export const useAvallinie = () => {
  const featureToggles = useFeatureToggles();

  const feature_Avallinien_S = featureToggles.useFeature(FeatureToggles.FEAT_01_AVALLINIEN_S);
  const feature_Avallinien_M = featureToggles.useFeature(FeatureToggles.FEAT_01_AVALLINIEN_M);
  const feature_Avallinien_L = featureToggles.useFeature(FeatureToggles.FEAT_01_AVALLINIEN_L);

  const supportsAvallinien = computed(() => {
    return feature_Avallinien_S.value || feature_Avallinien_M.value || feature_Avallinien_L.value;
  });

  const mehrAvallinienErlaubt = () => {
    const resultRef = ref(false);
    useAuftraggeberApi()
      .avallinie.canCreateNew()
      .then((result) => {
        resultRef.value = result;
      });
    return resultRef;
  };

  return {
    FeatureToggles,
    mehrAvallinienErlaubt,
    supportsAvallinien,
  };
};

export const centsToEuro = (avallinie: Avallinie): Avallinie => {
  avallinie.gesamtwert /= 100.0;
  avallinie.einzelwert /= 100.0;
  avallinie.auslastungStichtag /= 100.0;
  avallinie.sublimits.forEach((sublimit) => {
    sublimit.gesamtwert /= 100.0;
    sublimit.einzelwert /= 100.0;
    sublimit.auslastungStichtag /= 100.0;
  });

  return avallinie;
};

export const euroToCents = (avallinie: Avallinie): Avallinie => {
  const toCentsOrNull = (v: number | null | undefined): number | null =>
    v == null ? null : v * 100;

  avallinie.gesamtwert = toCentsOrNull(avallinie.gesamtwert);
  avallinie.einzelwert = toCentsOrNull(avallinie.einzelwert);
  avallinie.auslastungStichtag = toCentsOrNull(avallinie.auslastungStichtag);

  (avallinie.sublimits ?? []).forEach((sublimit) => {
    sublimit.gesamtwert = toCentsOrNull(sublimit.gesamtwert);
    sublimit.einzelwert = toCentsOrNull(sublimit.einzelwert);
    sublimit.auslastungStichtag = toCentsOrNull(sublimit.auslastungStichtag);
  });

  return avallinie;
};

export const createEmptySublimit = (): Sublimit => ({
  id: null,
  gesamtwert: null,
  einzelwert: null,
  gesamtwertAnteil: null,
  modus: SUBLIMIT_ANTEIL.GESAMTWERT,
  stichtag: null,
  auslastungStichtag: null,
  zinssatz: null,
  buergschaftsarten: [],
  auslastung: null,
  datumLetzteAusstellung: null,
});

export const mapAvallinieToCreate = (avallinie: Avallinie): Avallinie => {
  // Im Falle des Jahresrahmen haben wir einige Informationen nicht. Auch wenn wir das im Backend
  // eh sicher stellen, machen wir es hier direkt auch nochmal.
  // Umgekehrt ergeben sich auch ein paar Implikationen, die wir hier abbilden wollen.
  const isRahmenVertragsModellJahresrahmen = avallinie.modell === RahmenVertragsModell.JAHRESRAHMEN;

  return {
    ...avallinie,
    modell: avallinie.modell === null ? RahmenVertragsModell.GESAMTRAHMEN : avallinie.modell,
    kalenderjahr: isRahmenVertragsModellJahresrahmen ? avallinie.kalenderjahr : null,
    stichtag:
      avallinie.auslastungStichtag != null &&
      avallinie.auslastungStichtag !== 0 &&
      !isRahmenVertragsModellJahresrahmen
        ? formatDateTime(avallinie.stichtag)
        : null,
    auslastung: null,
    auslastungStichtag:
      avallinie.auslastungStichtag === 0 || isRahmenVertragsModellJahresrahmen
        ? null
        : avallinie.auslastungStichtag,
    zinssatz:
      avallinie.zinssatz === 0 || isRahmenVertragsModellJahresrahmen ? null : avallinie.zinssatz,
    sublimits: (avallinie.sublimits ?? []).map(
      ({
        zinssatz,
        modus,
        gesamtwert,
        gesamtwertAnteil,
        stichtag,
        auslastungStichtag,
        ...rest
      }) => ({
        ...rest,
        modus,
        gesamtwert: modus === SUBLIMIT_ANTEIL.GESAMTWERT ? gesamtwert : null,
        gesamtwertAnteil: modus === SUBLIMIT_ANTEIL.ANTEIL ? gesamtwertAnteil : null,
        auslastungStichtag:
          auslastungStichtag === 0 || isRahmenVertragsModellJahresrahmen
            ? null
            : auslastungStichtag,
        zinssatz: zinssatz === 0 || isRahmenVertragsModellJahresrahmen ? null : zinssatz,
        stichtag:
          auslastungStichtag != null && auslastungStichtag !== 0 ? formatDateTime(stichtag) : null,
      }),
    ),
  };
};
export const mapAvallinieFromApi = (data: Avallinie): Avallinie => ({
  ...data,
  stichtag: dateTimeFormatter.formatDate(data?.stichtag),
  sublimits: (data?.sublimits ?? []).map((s) => ({
    ...s,
    stichtag: dateTimeFormatter.formatDate(s?.stichtag),
  })),
});
